/*
 * Custom Members List Card that shows job title and office location for each member
 */

import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  InfoCardVariants,
  Link,
  Progress,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import WorkIcon from '@material-ui/icons/Work';
import { useAsync } from 'react-use';

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    marginTop: theme.spacing(2),
  },
  memberCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  memberCardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  avatar: {
    width: theme.spacing(8),
    height: theme.spacing(8),
    marginBottom: theme.spacing(2),
  },
  memberName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  memberInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    width: '100%',
    marginTop: theme.spacing(1),
    alignItems: 'flex-start',
  },
  metadata: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  icon: {
    fontSize: '1rem',
  },
  noMembers: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

export interface CustomMembersListCardProps {
  variant?: InfoCardVariants;
  /** Card heading. Defaults to "Members". */
  title?: string;
  /** Member refs to display. Defaults to entity.spec.members when omitted. */
  memberRefs?: string[];
}

export const CustomMembersListCard = (props: CustomMembersListCardProps) => {
  const { variant = 'gridItem', title = 'Members', memberRefs: memberRefsProp } = props;
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const rawMembers: unknown[] = memberRefsProp ?? (Array.isArray(entity.spec?.members) ? (entity.spec.members as unknown[]) : []);
  const members = rawMembers.filter((m): m is string => typeof m === 'string');

  // Fetch all member entities to get their profiles
  const { value: memberEntities, loading } = useAsync(async () => {
    if (members.length === 0) return [];

    const entities = await Promise.all(
      members.map(async (memberRef) => {
        try {
          // Parse member reference (could be "user:default/name" or just "name")
          const [kind, rest] = memberRef.includes(':')
            ? memberRef.split(':', 2)
            : ['user', memberRef];
          const [namespace, name] = rest.includes('/')
            ? rest.split('/', 2)
            : ['default', rest];

          const memberEntity = await catalogApi.getEntityByRef({
            kind,
            namespace,
            name,
          });

          return memberEntity;
        } catch (error) {
          return undefined;
        }
      })
    );

    return entities.filter((e): e is Entity => e !== undefined);
  }, [members.join(',')]);

  if (loading) {
    return <Progress />;
  }

  const sortedMembers = (memberEntities || []).sort((a, b) => {
    const aName = (a.spec?.profile as any)?.displayName || a.metadata.name;
    const bName = (b.spec?.profile as any)?.displayName || b.metadata.name;
    return aName.localeCompare(bName);
  });

  return (
    <InfoCard title={title} variant={variant}>
      {sortedMembers.length === 0 ? (
        <Typography className={classes.noMembers}>
          No members found
        </Typography>
      ) : (
        <Grid container spacing={2} className={classes.gridContainer}>
          {sortedMembers.map((member) => {
            const profile = member.spec?.profile as any;
            const displayName = profile?.displayName || member.metadata.name;
            const picture = profile?.picture;
            const jobTitle = profile?.jobTitle;
            const usageLocation = profile?.usageLocation;
            const memberLink = `/catalog/${member.metadata.namespace || 'default'}/user/${member.metadata.name}`;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${member.metadata.namespace ?? 'default'}/${member.metadata.name}`}>
                <Card className={classes.memberCard}>
                  <CardContent className={classes.memberCardContent}>
                    <Avatar
                      src={picture}
                      alt={displayName}
                      className={classes.avatar}
                    />
                    <Link to={memberLink} className={classes.memberName}>
                      <Typography variant="subtitle1" className={classes.memberName}>
                        {displayName}
                      </Typography>
                    </Link>
                    <Box className={classes.memberInfo}>
                      {jobTitle && (
                        <Box className={classes.metadata}>
                          <WorkIcon className={classes.icon} />
                          <Box>
                            {jobTitle.split(',').map((segment: string, index: number) => (
                              <Typography
                                key={index}
                                variant="body2"
                                style={{ textAlign: 'left', lineHeight: '1.2' }}
                              >
                                {segment.trim()}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {usageLocation && (
                        <Box className={classes.metadata}>
                          <LocationOnIcon className={classes.icon} />
                          <Typography variant="body2">
                            {usageLocation}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </InfoCard>
  );
};
